import { useCallback } from 'react';

import { useAddNote, useDeleteNote, useUpdateNote } from '/api';
import { snackbarService } from '/common/snackbarService';
import { useCommentDialog } from '/shared/EntityCommentDialog';

import { notesDialogOptions } from '../constants';
import { useBeetleLookupContext } from '../context';

import { useBeetleLookupCommonInfo } from './useBeetleLookupInfo';

export const useNotesInfo = () => {
  const { generalId, cache } = useBeetleLookupContext();
  const [commentDialogNode, openCommentDialog] = useCommentDialog();

  const {
    params: { env, type },
  } = useBeetleLookupCommonInfo();

  const { mutate: addNote } = useAddNote(type, {
    onSuccess: (data, variables) => {
      cache.addNote({ id: data || '', note: variables.note });
      snackbarService.success('Note was created');
    },
  });
  const onAddNote = useCallback(async () => {
    const note = await openCommentDialog('', notesDialogOptions);

    if (note) {
      addNote({ id: generalId, note, env });
    }
  }, [addNote, env, generalId, openCommentDialog]);

  const {
    mutate: updateIssueNote,
    variables: updateNoteVariables,
    isPending: updateLoading,
  } = useUpdateNote(type, {
    onSuccess: (_, variables) => {
      cache.updateNote({ id: variables.noteId, note: variables.note });

      snackbarService.success('Note was updated');
    },
  });
  const onUpdateNote = useCallback(
    async (noteId: string, prevNote: string) => {
      const note = await openCommentDialog(prevNote, notesDialogOptions);
      if (note) {
        updateIssueNote({ id: generalId, noteId, note, env });
      }
    },
    [env, generalId, openCommentDialog, updateIssueNote]
  );

  const {
    mutate: deleteNote,
    variables: deleteNoteVariables,
    isPending: deleteLoading,
  } = useDeleteNote(type, {
    onSuccess: (_, { noteId }) => {
      cache.removeNote(noteId);
      snackbarService.success('Note was deleted');
    },
  });
  const onDeleteNote = (noteId: string) => {
    deleteNote({ id: generalId, noteId, env });
  };

  return {
    variables: {
      deleteNoteVariables,
      updateNoteVariables,
      deleteLoading,
      updateLoading,
    },
    commentDialogNode,
    onUpdateNote,
    onAddNote,
    onDeleteNote,
  };
};
